import type { AgentIntegrationDefinition } from "./agentIntegrationDefinitions";

import * as React from "react";

import { skipToken } from "@tanstack/react-query";
import { useIsAgentEnabled } from "@v2/contexts/CurrentProjectContext";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { agentIntegrationsRouter } from "@v2/trpc/routes/agentIntegrations";
import { routes } from "@v2/utils/router";
import { generatePath, useNavigate } from "react-router-dom";

import {
  AGENT_INTEGRATIONS,
  getAgentIntegrationIcon,
} from "./agentIntegrationDefinitions";
import IntegrationStatusTag from "./IntegrationStatusTag";

function matchesAgentIntegrationSearch({
  integration,
  query,
}: {
  integration: AgentIntegrationDefinition;
  query: string;
}): boolean {
  const lowerQuery = query.toLowerCase();
  return (
    integration.title.toLowerCase().includes(lowerQuery) ||
    integration.description.toLowerCase().includes(lowerQuery)
  );
}

export const AgentIntegrationsSection: React.FC<{
  searchQuery: string;
}> = ({ searchQuery }) => {
  const navigate = useNavigate();
  const projectId = useCurrentProjectId();
  const isReploAgentEnabled = useIsAgentEnabled();

  const googleCalendarStatusQuery =
    agentIntegrationsRouter.getConnectionStatus.useQuery(
      projectId ? { integrationKey: "google-calendar", projectId } : skipToken,
    );
  const notionStatusQuery =
    agentIntegrationsRouter.getConnectionStatus.useQuery(
      projectId ? { integrationKey: "notion", projectId } : skipToken,
    );

  const connectionStatusByKey = {
    "google-calendar": googleCalendarStatusQuery.data,
    notion: notionStatusQuery.data,
  } as const;

  const enabledIntegrations = React.useMemo(() => {
    return AGENT_INTEGRATIONS.filter((integration) => {
      if (integration.requiresAgentEnabled) {
        return isReploAgentEnabled;
      }
      return true;
    });
  }, [isReploAgentEnabled]);

  const filteredIntegrations = React.useMemo(() => {
    if (!searchQuery.trim()) {
      return enabledIntegrations;
    }
    return enabledIntegrations.filter((integration) => {
      return matchesAgentIntegrationSearch({
        integration,
        query: searchQuery,
      });
    });
  }, [enabledIntegrations, searchQuery]);

  if (filteredIntegrations.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium text-default">Productivity</span>
      </div>
      <span className="text-sm text-muted -mt-2">
        Connect tools that can be used inside Replo Agent.
      </span>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredIntegrations.map((integration) => {
          const status = connectionStatusByKey[integration.key];
          const isConnected = status?.isConnected ?? false;

          return (
            <button
              type="button"
              key={integration.key}
              className="group relative flex flex-col gap-3 rounded-xl border-0.5 border-border p-4 transition-colors min-h-[140px] hover:bg-button-secondary-hover cursor-pointer shadow-xs text-left"
              onClick={() => {
                if (!projectId) {
                  return;
                }
                navigate(
                  generatePath(routes.shops.integrations.agentDetail, {
                    projectId,
                    integrationKey: integration.key,
                  }),
                );
              }}
            >
              <div className="flex items-start justify-between">
                <div className="size-9 rounded-lg bg-surface flex items-center justify-center">
                  {getAgentIntegrationIcon(integration.key)}
                </div>
                <IntegrationStatusTag
                  status={isConnected ? "connected" : "not_connected"}
                />
              </div>
              <div className="flex flex-col gap-0.5">
                <span className="text-sm font-medium text-default break-words">
                  {integration.title}
                </span>
                <span className="text-xs text-muted break-words leading-relaxed line-clamp-2">
                  {integration.description}
                </span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};
